/**
 * Debug seam for the performance door.
 *
 * The pre-move `pushDebugLog` read `NEXT_PUBLIC_PERF_DEBUG` and wrote a jotai
 * atom that only the app's `PerfDebugPanel` renders. Packages read no env
 * (G1/F5) and the panel is app UI, so the sink stays app-side and is registered
 * in here — the same shape as `registerAnalyticsDebugHook` on the `./analytics`
 * door.
 *
 * SAFE DEFAULT: no sink. Emits before app wiring are dropped, which is the
 * honest behaviour for a dev-only panel that the env flag disables outright in
 * every deployed build.
 */

export type PerformanceDebugEvent = {
  // `frame_episode` is emitted by the frame sampler beside the tracker's own
  // `finish`, because `finish`'s detail carries the total only and the panel
  // wants surface, frame count and dropped percentage on one line.
  type: 'start' | 'checkpoint' | 'finish' | 'cancel' | 'frame_episode';
  key: string;
  detail?: string;
  elapsedMs?: number;
};

export type PerformanceDebugSink = (event: PerformanceDebugEvent) => void;

let sink: PerformanceDebugSink | null = null;

/** One-time app-bootstrap call (src/composition/wiring/runtime-wiring.ts). */
export function registerPerformanceDebugSink(next: PerformanceDebugSink): void {
  sink = next;
}

export function emitPerformanceDebugEvent(event: PerformanceDebugEvent): void {
  if (!sink) return;
  // A throwing panel sink must not take down the tracking call that fed it —
  // every startTracking/finishTracking site in the app would inherit the crash.
  try {
    sink(event);
  } catch {
    // Dev-only telemetry; nothing to escalate to.
  }
}

export function __resetPerformanceDebugSinkForTests(): void {
  sink = null;
}
